import { useEffect, useState, useCallback } from "react";
import { Square, Clock, Loader2 } from "lucide-react";
import { Button } from "@repo/ui/button";
import { Badge } from "@repo/ui/badge";
import { Progress } from "@repo/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@repo/ui/lib/utils";
import { useNavigate } from "react-router-dom";
import { cancelJobRun } from "@/lib/api";
import { useJobRunCounts } from "@/hooks/useJobRunCounts";

interface ActiveRun {
  id: string;
  job_id: string;
  status: string;
  phase: string | null;
  started_at: string;
  records_scraped: number | null;
  urls_discovered: number | null;
  urls_scraped: number | null;
  job_name?: string;
  adapter_name?: string;
  metadata?: {
    total_urls?: number;
    phase?: string;
    current_url?: string;
  } | null;
}

const formatElapsedTime = (startedAt: string) => {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const diff = Math.floor((now - start) / 1000 / 60);
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

// Check if a job is potentially stale (running for over 15 minutes)
const isJobStale = (startedAt: string) => {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const diffMinutes = (now - start) / 1000 / 60;
  return diffMinutes > 15;
};

/**
 * Per-run row. Uses `useJobRunCounts` for fresh progress numbers derived
 * from scrape_queue (always accurate) rather than the cached
 * `job_runs.urls_*` columns which only the slow Supabase cron updates.
 */
const ActiveRunRow = ({
  run,
  canManageJobs,
  cancellingRunId,
  onCancel,
}: {
  run: ActiveRun;
  canManageJobs: boolean;
  cancellingRunId: string | null;
  onCancel: (id: string, name?: string) => void;
}) => {
  const counts = useJobRunCounts(run.id);
  // Prefer fresh scrape_queue-derived counts; fall back to cached columns
  // only when no queue rows exist yet (very early in a run).
  const discovered = counts.total > 0 ? counts.total : run.urls_discovered || 0;
  const scraped = counts.total > 0 ? counts.completed : run.urls_scraped || 0;
  const progressPercent = discovered > 0 ? Math.round((scraped / discovered) * 100) : 0;
  const phase = run.phase || run.metadata?.phase || "discovering";
  const stale = isJobStale(run.started_at);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Loader2 className={cn("w-4 h-4 animate-spin", stale ? "text-warning" : "text-accent")} />
            <span className="font-medium">{run.job_name || "Unknown Job"}</span>
            {stale && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge variant="outline" className="text-xs text-warning border-warning">
                      Slow
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This job has been running for a while. It may have stalled.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span>{run.adapter_name}</span>
            <span>•</span>
            <span className={stale ? "text-warning" : ""}>{formatElapsedTime(run.started_at)} elapsed</span>
          </div>
        </div>
        {canManageJobs ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => onCancel(run.id, run.job_name)}
            disabled={cancellingRunId === run.id}
            type="button"
          >
            {cancellingRunId === run.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Square className="w-4 h-4" />
            )}
          </Button>
        ) : null}
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground capitalize">
            {phase === "discovery" || phase === "discovering"
              ? `Discovering URLs... ${discovered > 0 ? `(${discovered} found)` : ""}`
              : phase === "paused"
              ? "Paused"
              : "Scraping properties"}
          </span>
          <span className="font-medium text-accent">
            {discovered > 0
              ? `${scraped} / ${discovered} (${progressPercent}%)`
              : `${run.records_scraped || 0} scraped`}
          </span>
        </div>
        <Progress
          value={discovered > 0 ? progressPercent : undefined}
          className={cn("h-1.5", discovered === 0 && "animate-pulse")}
        />
      </div>
    </div>
  );
};

export const ActiveJobsCard = () => {
  const { canManageJobs } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);

  const handleCancel = async (runId: string, jobName?: string) => {
    try {
      setCancellingRunId(runId);
      const result = await cancelJobRun(runId);

      if (result.success) {
        toast({
          title: "Job Cancelled",
          description: jobName ? `${jobName} has been cancelled.` : "The job run has been cancelled.",
        });
      } else {
        toast({
          title: "Cancel Failed",
          description: result.error || "Failed to cancel the job run.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Cancel job run error:", error);
      toast({
        title: "Cancel Failed",
        description: "Failed to cancel the job run.",
        variant: "destructive",
      });
    } finally {
      setCancellingRunId(null);
    }
  };

  const loadActiveRuns = useCallback(async () => {
    const { data } = await (supabase as any)
      .from('job_runs')
      .select(`
        id,
        job_id,
        status,
        phase,
        started_at,
        records_scraped,
        urls_discovered,
        urls_scraped,
        metadata,
        scrape_jobs!inner(name, adapters(name))
      `)
      .eq('status', 'running');

    if (data) {
      const runs = data.map((run: any) => ({
        id: run.id,
        job_id: run.job_id,
        status: run.status,
        phase: run.phase,
        started_at: run.started_at,
        records_scraped: run.records_scraped,
        urls_discovered: run.urls_discovered,
        urls_scraped: run.urls_scraped,
        job_name: run.scrape_jobs?.name,
        adapter_name: run.scrape_jobs?.adapters?.name,
        metadata: run.metadata,
      }));
      setActiveRuns(runs);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadActiveRuns();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('active_runs_dashboard')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_runs',
        },
        (payload) => {
          console.log('ActiveJobsCard realtime update:', payload);
          loadActiveRuns();
        }
      )
      .subscribe((status) => {
        console.log('ActiveJobsCard subscription status:', status);
      });

    // Update elapsed time every 30 seconds
    const interval = setInterval(() => {
      setActiveRuns(prev => [...prev]);
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [loadActiveRuns]);

  return (
    <div className="card-elevated">
      <div className="p-5 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Active Jobs</h3>
            {activeRuns.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeRuns.length} running
              </Badge>
            )}
          </div>
          {canManageJobs ? (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/dashboard/jobs')}
            >
              New Job
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="outline" className="text-xs">
                    View Only
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>You have read-only access</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
          </div>
        ) : activeRuns.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active jobs</p>
          </div>
        ) : (
          activeRuns.map((run) => (
            <ActiveRunRow
              key={run.id}
              run={run}
              canManageJobs={canManageJobs}
              cancellingRunId={cancellingRunId}
              onCancel={handleCancel}
            />
          ))
        )}
      </div>
    </div>
  );
};
